import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, Pressable, View } from 'react-native';
import Stack from '../Stack';
import Text, { TextColor, TextVariant } from '../Text';
import { ANIMATION_DURATION, TAB_CARD_ITEM_WIDTH } from './constants';
import { colors } from '../../../utils/theme';
import { StateStyles, useInteractionStateStyles } from '../../../hooks/useInteractionStateStyles';
import { getTabButtonTestId } from './utils';
import Icon, { IconName } from '../Icon';

export type SideTabButtonOwnProps = {
    isSelected: boolean;
    isDisabled: boolean;
    onClick: () => void;
    title: string;
    value: string;
    secondaryText?: string;
    iconName?: IconName;
    inset?: boolean;
    heavy?: boolean;
};

export type SideTabButtonProps = SideTabButtonOwnProps;

type StateStyle = {
    backgroundColor: string;
    isBold: boolean;
    indicatorWidth: number | null;
    textColor?: TextColor;
};

const stateStyles: StateStyles<StateStyle> = {
    default: {
        backgroundColor: colors.White,
        isBold: false,
        indicatorWidth: null,
    },
    hovered: {
        backgroundColor: colors['Grey-Light2'],
        isBold: false,
        indicatorWidth: null,
    },
    pressed: {
        backgroundColor: colors['Grey-Light2'],
        isBold: true,
        indicatorWidth: 2,
    },
    inactive: {
        backgroundColor: colors['Grey-Light2'],
        isBold: false,
        indicatorWidth: 0,
        textColor: 'Gray-Text',
    },
};

export const SideTabButton: React.FC<SideTabButtonProps> = ({
    isSelected,
    isDisabled,
    onClick,
    title,
    value,
    secondaryText,
    iconName,
    inset,
    heavy,
}) => {
    const { styles, onPressIn, onPressOut, renderInHoverContainer } = useInteractionStateStyles({
        isDisabled,
        stateStyles,
    });
    const indicatorAnimationRef = useRef<Animated.Value>(new Animated.Value(isSelected ? 1 : 0));
    useEffect(() => {
        Animated.timing(indicatorAnimationRef.current, {
            toValue: isSelected ? 1 : 0,
            duration: ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
            easing: Easing.inOut(Easing.ease),
        }).start();
    }, [isSelected]);

    const getTextVariant = (isSelected: boolean): TextVariant => {
        if (heavy) {
            return isSelected ? 'bold-h4' : 'h4';
        } else {
            return isSelected ? 'bold-body-1' : 'body-1';
        }
    };

    return renderInHoverContainer(
        <View style={{ width: TAB_CARD_ITEM_WIDTH, paddingLeft: inset ? 10 : 0 }}>
            <Pressable
                onPressIn={onPressIn}
                onPressOut={onPressOut}
                onPress={onClick}
                disabled={isDisabled}
                testID={getTabButtonTestId(value, isDisabled)}
            >
                <Stack
                    align="flex-start"
                    vertical
                    space={2}
                    style={{
                        backgroundColor: styles.backgroundColor,
                        paddingTop: 8,
                    }}
                >
                    <Stack
                        horizontal
                        fullWidth
                        justify="space-between"
                        style={{
                            paddingRight: 8,
                            paddingLeft: 8,
                        }}
                        align="flex-end"
                    >
                        <Stack horizontal space={4} align="center">
                            {iconName ? <Icon name={iconName} size="side-tab-icon" color="Primary" /> : null}
                            <Text variant={getTextVariant(styles.isBold || isSelected)} color={styles.textColor}>
                                {title}
                            </Text>
                        </Stack>
                        {secondaryText ? (
                            <Text variant="caption-1" color={styles.isBold || isSelected ? 'Dark-Text' : 'Gray-Text'}>
                                {secondaryText}
                            </Text>
                        ) : null}
                    </Stack>
                    <View
                        style={{
                            width: '100%',
                            height: 2,
                            backgroundColor: colors.Dividers,
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Animated.View
                            style={{
                                width: styles.indicatorWidth
                                    ? styles.indicatorWidth
                                    : indicatorAnimationRef.current.interpolate({
                                          inputRange: [0, 1],
                                          outputRange: ['0%', '100%'],
                                      }),
                                height: '100%',
                                backgroundColor: colors['Primary-Reg'],
                            }}
                        />
                    </View>
                </Stack>
            </Pressable>
        </View>,
    );
};

export default React.memo(SideTabButton);
